import { CartAddEvent, CartErrorEvent } from '@theme/events';

const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/** Dialogs (phone menu, size guide) open from any [data-hs-open] trigger. */
document.addEventListener('click', (event) => {
  const target = event.target instanceof Element ? event.target : null;
  if (!target) return;
  const opener = target.closest('[data-hs-open]');
  if (opener) {
    const dialog = document.getElementById(opener.getAttribute('data-hs-open') ?? '');
    if (dialog instanceof HTMLDialogElement) {
      event.preventDefault();
      dialog.showModal();
    }
    return;
  }
  const closer = target.closest('[data-hs-close]');
  if (closer) {
    closer.closest('dialog')?.close();
    return;
  }
  if (target instanceof HTMLDialogElement && target.matches('.hs-drawer, .hs-dialog')) {
    const box = target.getBoundingClientRect();
    if (event instanceof MouseEvent && (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom)) target.close();
  }
});

/** Horizontal rails with arrow buttons on desktop and native swipe on phones. */
class HayaRail {
  /** @param {HTMLElement} root */
  constructor(root) {
    this.track = root.querySelector('[data-hs-rail-track]');
    this.prev = root.querySelector('[data-hs-rail-prev]');
    this.next = root.querySelector('[data-hs-rail-next]');
    if (!(this.track instanceof HTMLElement)) return;
    this.prev?.addEventListener('click', () => this.step(-1));
    this.next?.addEventListener('click', () => this.step(1));
    this.track.addEventListener('scroll', () => this.update(), { passive: true });
    new ResizeObserver(() => this.update()).observe(this.track);
    this.update();
  }

  /** @param {number} direction */
  step(direction) {
    if (!(this.track instanceof HTMLElement)) return;
    this.track.scrollBy({ left: direction * this.track.clientWidth * 0.85, behavior: reduceMotion ? 'auto' : 'smooth' });
  }

  update() {
    if (!(this.track instanceof HTMLElement)) return;
    const max = this.track.scrollWidth - this.track.clientWidth - 2;
    if (this.prev instanceof HTMLButtonElement) this.prev.disabled = this.track.scrollLeft <= 2;
    if (this.next instanceof HTMLButtonElement) this.next.disabled = this.track.scrollLeft >= max;
  }
}

/** Hero carousel: numbered buttons, swipe, and gentle autoplay that stops once the shopper interacts. */
class HayaHero {
  /** @param {HTMLElement} root */
  constructor(root) {
    this.root = root;
    this.track = root.querySelector('[data-hs-hero-track]');
    this.dots = [...root.querySelectorAll('[data-hs-hero-dot]')];
    if (!(this.track instanceof HTMLElement) || this.dots.length < 2) return;
    this.index = 0;
    this.dots.forEach((dot, index) => dot.addEventListener('click', () => { this.stop(); this.go(index); }));
    this.track.addEventListener('scroll', () => {
      if (!(this.track instanceof HTMLElement)) return;
      const index = Math.round(this.track.scrollLeft / this.track.clientWidth);
      if (index !== this.index) this.mark(index);
    }, { passive: true });
    for (const name of ['pointerdown', 'focusin', 'touchstart']) root.addEventListener(name, () => this.stop(), { passive: true });
    const seconds = Number(root.dataset.autoplay) || 0;
    if (seconds > 0 && !reduceMotion) this.timer = window.setInterval(() => this.go((this.index + 1) % this.dots.length), seconds * 1000);
  }

  /** @param {number} index */
  go(index) {
    if (!(this.track instanceof HTMLElement)) return;
    this.track.scrollTo({ left: index * this.track.clientWidth, behavior: reduceMotion ? 'auto' : 'smooth' });
    this.mark(index);
  }

  /** @param {number} index */
  mark(index) {
    this.index = index;
    this.dots.forEach((dot, i) => dot.setAttribute('aria-current', String(i === index)));
  }

  stop() {
    if (this.timer) window.clearInterval(this.timer);
    this.timer = undefined;
  }
}

/** Front/Back compare slider; auto sliders glide on their own while visible until the shopper drags them. */
function initCompare(root) {
  const range = root.querySelector('[data-hs-compare-range]');
  if (!(range instanceof HTMLInputElement)) return;
  const set = (value) => root.style.setProperty('--hs-pos', `${value}%`);
  let frame = 0;
  let stopped = false;
  const stop = () => {
    stopped = true;
    cancelAnimationFrame(frame);
  };
  range.addEventListener('input', () => {
    stop();
    set(range.value);
  });
  range.addEventListener('pointerdown', stop);
  set(range.value);
  if (!root.hasAttribute('data-hs-compare-auto') || reduceMotion) return;
  let origin = 0;
  const tick = (time) => {
    if (!origin) origin = time;
    const value = 50 - 36 * Math.cos(((time - origin) / 6000) * Math.PI * 2);
    set(value.toFixed(2));
    range.value = String(Math.round(value));
    frame = requestAnimationFrame(tick);
  };
  new IntersectionObserver(([entry]) => {
    cancelAnimationFrame(frame);
    if (entry.isIntersecting && !stopped) {
      origin = 0;
      frame = requestAnimationFrame(tick);
    }
  }, { threshold: 0.3 }).observe(root);
}

/** Product gallery: swipe on phones, arrows and thumbnails on desktop. */
class HayaGallery {
  /** @param {HTMLElement} root */
  constructor(root) {
    this.track = root.querySelector('[data-hs-gal-track]');
    this.thumbs = [...root.querySelectorAll('[data-hs-gal-thumb]')];
    this.current = root.querySelector('[data-hs-gal-current]');
    this.prev = root.querySelector('[data-hs-gal-prev]');
    this.next = root.querySelector('[data-hs-gal-next]');
    if (!(this.track instanceof HTMLElement)) return;
    this.index = 0;
    this.prev?.addEventListener('click', () => this.go(this.index - 1));
    this.next?.addEventListener('click', () => this.go(this.index + 1));
    this.thumbs.forEach((thumb, i) => thumb.addEventListener('click', () => this.go(i)));
    this.track.addEventListener('scroll', () => {
      if (!(this.track instanceof HTMLElement)) return;
      const index = Math.round(this.track.scrollLeft / this.track.clientWidth);
      if (index !== this.index) this.mark(index);
    }, { passive: true });
    this.mark(0);
  }

  /** @param {number} index */
  go(index) {
    if (!(this.track instanceof HTMLElement)) return;
    const max = this.track.children.length - 1;
    const target = Math.max(0, Math.min(max, index));
    this.track.scrollTo({ left: target * this.track.clientWidth, behavior: reduceMotion ? 'auto' : 'smooth' });
    this.mark(target);
  }

  /** @param {number} index */
  mark(index) {
    this.index = index;
    this.thumbs.forEach((thumb, i) => thumb.setAttribute('aria-current', String(i === index)));
    const thumb = this.thumbs[index];
    const strip = thumb?.parentElement;
    if (thumb instanceof HTMLElement && strip) strip.scrollTo({ left: thumb.offsetLeft - strip.clientWidth / 2 + thumb.clientWidth / 2, behavior: 'auto' });
    if (this.current) this.current.textContent = String(index + 1);
    const max = this.track instanceof HTMLElement ? this.track.children.length - 1 : 0;
    if (this.prev instanceof HTMLButtonElement) this.prev.disabled = index <= 0;
    if (this.next instanceof HTMLButtonElement) this.next.disabled = index >= max;
  }
}

function init(scope = document) {
  measureHeader();
  for (const el of scope.querySelectorAll('[data-hs-rail]:not([data-hs-ready])')) {
    el.setAttribute('data-hs-ready', '');
    new HayaRail(/** @type {HTMLElement} */ (el));
  }
  for (const el of scope.querySelectorAll('[data-hs-hero]:not([data-hs-ready])')) {
    el.setAttribute('data-hs-ready', '');
    new HayaHero(/** @type {HTMLElement} */ (el));
  }
  for (const el of scope.querySelectorAll('[data-hs-gallery]:not([data-hs-ready])')) {
    el.setAttribute('data-hs-ready', '');
    new HayaGallery(/** @type {HTMLElement} */ (el));
  }
  for (const el of scope.querySelectorAll('[data-hs-compare]:not([data-hs-ready])')) {
    el.setAttribute('data-hs-ready', '');
    initCompare(el);
  }
}

/** Collection page: sort and desktop filters apply as soon as they change. */
document.addEventListener('change', (event) => {
  const target = event.target;
  if (target instanceof HTMLSelectElement && target.matches('[data-hs-autosubmit]')) {
    const url = new URL(window.location.href);
    url.searchParams.set('sort_by', target.value);
    url.searchParams.delete('page');
    window.location.assign(url.toString());
    return;
  }
  if (!(target instanceof HTMLInputElement)) return;
  const form = target.form;
  if (!form?.hasAttribute('data-hs-autosubmit-form')) return;
  window.clearTimeout(Number(form.dataset.timer));
  form.dataset.timer = String(window.setTimeout(() => form.requestSubmit(), target.type === 'number' ? 700 : 0));
});

/** Filter forms submit clean URLs: no empty price fields, one sort value, back to page 1. */
document.addEventListener('submit', (event) => {
  const form = event.target;
  if (!(form instanceof HTMLFormElement) || !form.classList.contains('hs-filters')) return;
  event.preventDefault();
  const params = new URLSearchParams();
  for (const [key, value] of new FormData(form)) {
    if (typeof value !== 'string' || value === '') continue;
    if (key === 'sort_by') params.set(key, value);
    else params.append(key, value);
  }
  window.location.assign(`${form.action}?${params.toString()}`);
});

/** Keep sticky bars below the sticky header. */
function measureHeader() {
  const header = document.querySelector('.hs-header');
  if (header instanceof HTMLElement) document.documentElement.style.setProperty('--hs-header-h', `${Math.round(header.offsetHeight)}px`);
}
window.addEventListener('resize', measureHeader, { passive: true });

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
else init();
document.addEventListener('shopify:section:load', (event) => init(/** @type {HTMLElement} */ (event.target)));

/**
 * Product form: resolves the variant from option chips, keeps price, stock, URL and gallery in sync,
 * asks for a size before adding, and adds through /cart/add.js so the theme cart drawer updates.
 */
class HayaProduct extends HTMLElement {
  connectedCallback() {
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.variants = JSON.parse(this.querySelector('[data-hs-variants]')?.textContent ?? '[]');
    this.strings = JSON.parse(this.querySelector('[data-hs-strings]')?.textContent ?? '{}');
    this.form = this.querySelector('form.hs-pdp__form');
    this.idInput = this.querySelector('[data-hs-variant-id]');
    this.sizeIndex = Number(this.dataset.sizeIndex);
    this.addEventListener('change', (event) => {
      if (event.target instanceof HTMLInputElement && event.target.matches('[data-hs-value]')) this.onOptionChange();
    }, { signal });
    this.addEventListener('click', (event) => {
      const target = event.target instanceof Element ? event.target : null;
      const qty = target?.closest('[data-hs-qty]');
      if (qty) this.stepQuantity(Number(qty.getAttribute('data-hs-qty')));
      if (target?.closest('[data-hs-sticky-add]')) this.stickyAdd();
      const wa = target?.closest('[data-hs-wa-order]');
      if (wa instanceof HTMLAnchorElement) this.orderOnWhatsApp(event, wa);
    }, { signal });
    this.form?.addEventListener('submit', (event) => this.add(event), { signal });
    this.watchSticky();
    this.refreshAvailability();
  }

  disconnectedCallback() {
    this.controller?.abort();
  }

  /** @returns {(string | null)[]} */
  selectedOptions() {
    return [...this.querySelectorAll('[data-hs-option]')].map((fieldset) => {
      const checked = fieldset.querySelector('[data-hs-value]:checked');
      return checked instanceof HTMLInputElement ? checked.value : null;
    });
  }

  currentVariant() {
    const selected = this.selectedOptions();
    if (selected.some((value) => value === null)) return null;
    return this.variants.find((variant) => variant.options.every((value, i) => value === selected[i])) ?? null;
  }

  onOptionChange() {
    const selected = this.selectedOptions();
    this.querySelectorAll('[data-hs-option]').forEach((fieldset, i) => {
      const label = fieldset.querySelector('[data-hs-option-value]');
      if (label) label.textContent = selected[i] ? `· ${selected[i]}` : '';
    });
    const need = this.querySelector('[data-hs-need-size]');
    if (need && !selected.some((value) => value === null)) need.hidden = true;
    this.refreshAvailability();
    this.render(this.currentVariant());
  }

  /** Cross out values that cannot be bought with the other current choices. */
  refreshAvailability() {
    const selected = this.selectedOptions();
    this.querySelectorAll('[data-hs-option]').forEach((fieldset, optionIndex) => {
      for (const input of fieldset.querySelectorAll('[data-hs-value]')) {
        if (!(input instanceof HTMLInputElement)) continue;
        const buyable = this.variants.some((variant) => variant.available && variant.options[optionIndex] === input.value && variant.options.every((value, i) => i === optionIndex || selected[i] === null || selected[i] === value));
        input.closest('.hs-chip')?.classList.toggle('hs-chip--na', !buyable);
      }
    });
  }

  /** @param {any} variant */
  render(variant) {
    const s = this.strings;
    const add = this.querySelector('[data-hs-add]');
    const addLabel = this.querySelector('[data-hs-add-label]');
    const stock = this.querySelector('[data-hs-stock]');
    const sticky = this.querySelector('[data-hs-sticky-add]');
    const choice = this.querySelector('[data-hs-sticky-choice]');
    if (!variant) {
      if (this.selectedOptions().every((value) => value !== null)) {
        if (addLabel) addLabel.textContent = s.unavailable;
        if (add instanceof HTMLButtonElement) add.disabled = true;
      }
      if (this.idInput instanceof HTMLInputElement) this.idInput.value = '';
      return;
    }
    if (this.idInput instanceof HTMLInputElement) this.idInput.value = String(variant.id);
    for (const price of this.querySelectorAll('[data-hs-price]')) price.textContent = variant.price;
    const compare = this.querySelector('[data-hs-compare-price]');
    const off = this.querySelector('[data-hs-off]');
    if (compare instanceof HTMLElement) {
      compare.hidden = !variant.compare;
      const hidden = compare.querySelector('.hs-visually-hidden');
      compare.textContent = variant.compare ?? '';
      if (hidden) compare.prepend(hidden);
    }
    if (off instanceof HTMLElement) {
      off.hidden = !variant.off;
      off.textContent = String(s.off ?? '').replace('__P__', String(variant.off));
    }
    const sku = this.querySelector('[data-hs-sku]');
    if (sku && variant.sku) sku.textContent = variant.sku;
    const skuLine = this.querySelector('[data-hs-sku-line]');
    const skuValue = this.querySelector('[data-hs-sku-value]');
    if (skuLine instanceof HTMLElement && skuValue && variant.sku) {
      skuValue.textContent = variant.sku;
      skuLine.dataset.state = 'selected';
    }
    if (stock instanceof HTMLElement) {
      stock.hidden = false;
      stock.dataset.state = variant.available ? (variant.low ? 'low' : 'ok') : 'out';
      stock.textContent = variant.available ? (variant.low ? String(s.low).replace('__N__', String(variant.low)) : s.inStock) : s.soldOut;
    }
    if (add instanceof HTMLButtonElement) add.disabled = !variant.available;
    if (addLabel) addLabel.textContent = variant.available ? s.add : s.soldOut;
    if (sticky instanceof HTMLButtonElement) {
      sticky.disabled = !variant.available;
      sticky.textContent = variant.available ? s.addShort : s.soldOut;
    }
    if (choice) choice.textContent = variant.title;
    const url = new URL(window.location.href);
    url.searchParams.set('variant', String(variant.id));
    window.history.replaceState(window.history.state, '', url.toString());
    this.showMedia(variant.media);
  }

  /** @param {number | null} mediaId */
  showMedia(mediaId) {
    if (!mediaId) return;
    const gallery = /** @type {any} */ (this.querySelector('media-gallery'));
    if (!gallery?.media || !gallery.slideshow) return;
    const index = gallery.media.findIndex((media) => media.querySelector('[data-media-id]')?.getAttribute('data-media-id') === String(mediaId));
    if (index >= 0) gallery.slideshow.select(index, undefined, { animate: !reduceMotion });
  }

  /** @param {number} delta */
  stepQuantity(delta) {
    const input = this.querySelector('input[name="quantity"]');
    if (!(input instanceof HTMLInputElement)) return;
    const min = Number(input.min) || 1;
    input.value = String(Math.max(min, (Number(input.value) || min) + delta));
  }

  askForSize() {
    const need = this.querySelector('[data-hs-need-size]');
    if (need instanceof HTMLElement) need.hidden = false;
    const missing = [...this.querySelectorAll('[data-hs-option]')].find((fieldset) => !fieldset.querySelector('[data-hs-value]:checked'));
    if (missing instanceof HTMLElement) {
      missing.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center' });
      const first = missing.querySelector('[data-hs-value]');
      if (first instanceof HTMLElement) window.setTimeout(() => first.focus({ preventScroll: true }), reduceMotion ? 0 : 350);
    }
  }

  stickyAdd() {
    if (!this.currentVariant()) {
      this.askForSize();
      return;
    }
    this.form?.requestSubmit();
  }

  /**
   * Opens WhatsApp with the product, chosen options, quantity, price and link prefilled.
   * @param {Event} event
   * @param {HTMLAnchorElement} link
   */
  orderOnWhatsApp(event, link) {
    const variant = this.currentVariant() ?? (this.variants.length === 1 ? this.variants[0] : null);
    if (!variant) {
      event.preventDefault();
      this.askForSize();
      return;
    }
    const s = this.strings;
    const number = this.querySelector('[data-wa-number]')?.getAttribute('data-wa-number') ?? '';
    const quantity = Number(this.querySelector('input[name="quantity"]')?.value) || 1;
    const names = [...this.querySelectorAll('[data-hs-option]')].map((fieldset) => fieldset.getAttribute('data-hs-option-name') ?? '');
    const lines = [`${s.waIntro}`, `*${s.title}*`];
    if (variant.title !== 'Default Title') variant.options.forEach((value, i) => lines.push(`${!names[i] || names[i].toLowerCase().includes('size') ? s.waSize : names[i]}: ${value}`));
    lines.push(`${s.waQty}: ${quantity}`, `${s.waPrice}: ${variant.price}`, `${s.url}?variant=${variant.id}`);
    link.href = `https://wa.me/${number}?text=${encodeURIComponent(lines.join('\n'))}`;
  }

  /** @param {SubmitEvent} event */
  async add(event) {
    event.preventDefault();
    const form = this.form;
    if (!(form instanceof HTMLFormElement) || this.busy) return;
    const variant = this.currentVariant() ?? (this.variants.length === 1 ? this.variants[0] : null);
    if (!variant) {
      this.askForSize();
      return;
    }
    if (!variant.available) return;
    if (this.idInput instanceof HTMLInputElement) this.idInput.value = String(variant.id);
    const status = this.querySelector('[data-hs-status]');
    const added = this.querySelector('[data-hs-added]');
    const buttons = [...this.querySelectorAll('[data-hs-add], [data-hs-sticky-add]')];
    const label = this.querySelector('[data-hs-add-label]');
    this.busy = true;
    buttons.forEach((button) => { if (button instanceof HTMLButtonElement) { button.disabled = true; button.setAttribute('aria-busy', 'true'); } });
    if (label) label.textContent = this.strings.adding;
    if (status) status.textContent = '';
    added?.removeAttribute('data-show');
    const data = new FormData(form);
    data.set('id', String(variant.id));
    const sectionIds = new Set();
    for (const item of document.querySelectorAll('cart-items-component')) {
      if (item instanceof HTMLElement && item.dataset.sectionId) sectionIds.add(item.dataset.sectionId);
    }
    if (sectionIds.size) {
      data.set('sections', [...sectionIds].join(','));
      data.set('sections_url', window.location.pathname);
    }
    try {
      const response = await fetch(`${window.Shopify?.routes?.root ?? '/'}cart/add.js`, { method: 'POST', headers: { Accept: 'application/json' }, body: data });
      const result = await response.json();
      if (!response.ok || result.status) throw new Error(result.description || result.message || this.strings.error);
      this.dispatchEvent(new CartAddEvent({}, String(variant.id), { source: 'product-form-component', productId: this.dataset.productId, itemCount: Number(data.get('quantity')) || 1, variantId: String(variant.id), sections: result.sections }));
      if (!document.getElementById('razorpay-sidecart-container')) added?.setAttribute('data-show', '');
    } catch (error) {
      const message = error instanceof Error ? error.message : this.strings.error;
      if (status) status.textContent = message;
      this.dispatchEvent(new CartErrorEvent(this.id, message));
    } finally {
      this.busy = false;
      buttons.forEach((button) => button.removeAttribute('aria-busy'));
      this.render(this.currentVariant() ?? variant);
    }
  }

  /** Show the phone buy bar once the main buy box scrolls out of view. */
  watchSticky() {
    const sticky = this.querySelector('[data-hs-sticky]');
    const box = this.querySelector('.hs-pdp__box');
    if (!(sticky instanceof HTMLElement) || !box) return;
    let frame = 0;
    const update = () => {
      frame = 0;
      const show = box.getBoundingClientRect().bottom < 0;
      if (show === sticky.hasAttribute('data-show')) return;
      sticky.toggleAttribute('data-show', show);
      sticky.setAttribute('aria-hidden', String(!show));
      sticky.querySelector('button')?.setAttribute('tabindex', show ? '0' : '-1');
    };
    window.addEventListener('scroll', () => { if (!frame) frame = requestAnimationFrame(update); }, { passive: true, signal: this.controller?.signal });
    update();
  }
}

if (!customElements.get('haya-product')) customElements.define('haya-product', HayaProduct);
